import React from "react";
import {
  Button,
  Header,
  Popover,
  Table,
  TableProps,
} from "@amzn/awsui-components-console";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
}

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

// Helper function to format numeric values
const formatNumber = (value: any) => {
  if (!value) return "-";
  return Number(value.string).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
};

export const InvoiceBreakdownTable: React.FC<Props> = ({ fixedFeeConfig }) => {
  if (
    !fixedFeeConfig.invoiceBreakdown ||
    Object.keys(fixedFeeConfig.invoiceBreakdown).length === 0
  ) {
    return null;
  }

  // Helper to get transaction links for popover
  const getResultPopover = (item: any) => {
    const billingPeriod =
      item.billingTermDescription ??
      `${item.periodStartDate} - ${item.periodEndDate}`;

    return (
      <Popover
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={
          <>
            <p>
              On date <b>{item.invoiceKey}</b>, a{" "}
              <b>
                {formatNumber(item.invoiceAmount)}{" "}
                {fixedFeeConfig.contractCurrencyCode}
              </b>{" "}
              invoice would be/have been sent to{" "}
              <b>{fixedFeeConfig.dealConfig?.resellerIds?.[0] ?? "UNKNOWN"}</b>{" "}
              with following invoice description:
            </p>
            <p>
              "Contractual Billing of <b>{fixedFeeConfig.invoiceFrequency}</b>{" "}
              Fixed Fee for Amazon's grant to Reseller of the ability to offer
              the <b>{fixedFeeConfig.serviceProvider}</b> Bundle Offer Payable
              in <b>{fixedFeeConfig.contractCurrencyCode}</b>. Billing period{" "}
              <b>{billingPeriod}</b>"
            </p>
          </>
        }
      >
        <Button variant="inline-link">Simulate</Button>
      </Popover>
    );
  };

  const invoiceColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    DUMMY_COLUMN_DEFINITION,
    {
      id: "invoiceResult",
      header: "Invoice Result",
      cell: (item: any) => getResultPopover(item),
      sortingField: "invoiceResult",
    },
    {
      id: "invoiceKey",
      header: "Invoice Date",
      cell: (item: any) => item.invoiceKey,
      sortingField: "invoiceKey",
    },
    {
      id: "periodStartDate",
      header: "Period Start Date",
      cell: (item: any) => item.periodStartDate,
      sortingField: "periodStartDate",
    },
    {
      id: "periodEndDate",
      header: "Period End Date",
      cell: (item: any) => item.periodEndDate,
      sortingField: "periodEndDate",
    },
    {
      id: "invoiceAmount",
      header: "Invoice Amount",
      cell: (item: any) => formatNumber(item.invoiceAmount),
      sortingField: "invoiceAmount",
    },
    {
      id: "billingTermDescription",
      header: "Billing Term Description",
      cell: (item: any) => item.billingTermDescription || "-",
      sortingField: "billingTermDescription",
    },
    DUMMY_COLUMN_DEFINITION,
  ];

  const invoiceData = Object.entries(fixedFeeConfig.invoiceBreakdown)
    .map(([key, invoice]) => ({
      invoiceKey: key,
      periodStartDate: invoice.periodStartDate || "-",
      periodEndDate: invoice.periodEndDate || "-",
      invoiceAmount: invoice.invoiceAmount,
      billingTermDescription: invoice.billingTermDescription,
    }))
    .sort((a, b) => a.invoiceKey.localeCompare(b.invoiceKey));

  return (
    <Table
      columnDefinitions={invoiceColumnDefinitions}
      items={invoiceData}
      wrapLines={false}
      resizableColumns={false}
      header={
        <Header
          counter={`(${Object.entries(fixedFeeConfig.invoiceBreakdown).length})`}
        >
          Invoice Breakdown
        </Header>
      }
    />
  );
};
